import {Button, Col, Flex, Row, Select, Space, Switch, Typography} from "antd";
import {ArrowCounterClockwiseIcon, CaretDownIcon} from "@phosphor-icons/react";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {ThemeContext} from "../../../../contexts/ThemeContext";
import {ThemeColorContext} from "../../../../contexts/ThemeColorContext";
import {FOOT, INCH, METER, UnitContext} from "../../../../contexts/UnitContext";
import {useCurrentUser} from "../../../../contexts/UserContext";
import {ColorPicker} from "../../../index";
import ProfileAvatar from "./ProfilePhoto";
import { LengthUnit } from "../../../../@types/types";
import { useViewerStore } from "../../../../store/store";
import DashboardModal from "../../Editor-Layout/oneBUTTONDashboard/DashboardModal";

type UserSettingsProps = {
    isUserSettingsModalOpen: boolean,
    setIsUserSettingsModalOpen: (open: boolean) => void
}

const UserSettings = (props: UserSettingsProps) => {
    const {isUserSettingsModalOpen, setIsUserSettingsModalOpen} = props;

    const {user} = useCurrentUser();
    const isAdminUser = user?.application_role_name === 'Admin';
    const {Title} = Typography;


    const { theme, setTheme } = useContext(ThemeContext);
    const {primary, setPrimary, secondary, setSecondary} = useContext(ThemeColorContext);
    const [resetP, setResetP] = useState(true);
    const {unit, roofUnit, treeUnit, setUnit, setRoofUnit, setObstructionUnit, setTreeUnit} = useContext(UnitContext);
    const isSimpleTreeLook = useViewerStore(state => state.isSimpleTreeLook);
    const sceneViewer = useViewerStore(state => state.sceneViewer);


    const roofUnitChangeHandler = (e: any) => {
        const foundOption = options.find(obj => e === obj.label);
        if (foundOption && setRoofUnit && setObstructionUnit) { //null checks
            const value = foundOption.value;
            setRoofUnit(value as LengthUnit);
            setObstructionUnit(value as LengthUnit);
        }
    };

    const treeUnitChangeHandler = (e: any) => {
        const foundOption = options.find(obj => e === obj.label);
        if (foundOption && setTreeUnit) { //null checks
            const value = foundOption.value;
            setTreeUnit(value as LengthUnit);
        }
    };

    const unitChangeHandler = (e: any) => {
        const foundOption = options.find(obj => e === obj.label);
        if (foundOption && setUnit) { //null checks
            const value = foundOption.value;
            setUnit(value as LengthUnit);
        }
    };

    const resetPrimary = () => {
        setPrimary("#538abc");
    }

    useEffect(() => {
        if (primary === "#538abc") {
            setResetP(false);
        } else {
            setResetP(true);
        }

    }, [primary, secondary]);

    const options = [
        {value: METER, label: 'm'},
        {value: FOOT, label: 'ft'},
        {value: INCH, label: 'in'}
    ];

    const changePrimaryColor = (color: string) => {
        setPrimary(color);
    }

    const changeSimpleTreeLook = (checked: boolean) => {
        try {
            localStorage.setItem('isSimpleTreeLook', checked ? 'true' : 'false');
        } catch {
            // ignore storage errors
        }
        useViewerStore.setState({isSimpleTreeLook: checked});
        sceneViewer?.setTreeVisualStyle?.();
    }


    const modalTheme = useMemo<'dark' | 'light'>(() => (theme === 'dark' ? 'dark' : 'light'), [theme]);
    const closeSettings = () => setIsUserSettingsModalOpen(false);

    return (
        <DashboardModal
            open={isUserSettingsModalOpen}
            onClose={closeSettings}
            title="User Settings"
            width={1120}
            theme={modalTheme}
            getContainer={() => document.body}
            className="user-settings-modal"
            rootClassName="user-settings-modal-root"
            footer={null}
        >
            <Row gutter={[28, 28]} className='user-settings'>
                <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
                    <Flex
                        align='center'
                        justify='space-between'
                        className='user-settings__header'
                    >
                        <Title level={4} style={{marginBottom: 0}}>User Profile</Title>
                        <ProfileAvatar/>

                    </Flex>


                    {/* ----- Profile info (read-only label/value rows) ----- */}
                    <div className='user-settings__profile'>
                        <div className='user-settings__profile-row'>
                            <span className='user-settings__profile-label'>Employee ID</span>
                            <span className='user-settings__profile-value'>{user?.user_Id ?? '-'}</span>
                        </div>
                        <div className='user-settings__profile-row'>
                            <span className='user-settings__profile-label'>Name</span>
                            <span className='user-settings__profile-value'>{user?.user_name ?? '-'}</span>
                        </div>
                        <div className='user-settings__profile-row'>
                            <span className='user-settings__profile-label'>User Role</span>
                            <span className='user-settings__profile-value'>{user?.application_role_name ?? '-'}</span>
                        </div>
                        <div className='user-settings__profile-row'>
                            <span className='user-settings__profile-label'>Email Address</span>
                            <span className='user-settings__profile-value'>{user?.email ?? '-'}</span>
                        </div>
                    </div>
                </Col>
                <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
                    <Title level={4} style={{marginBottom: 0}}>Settings</Title>
                    <Space direction='vertical' size='large'>
                        <Flex align='center' justify='space-between' className='user-settings__row'>
                            <div className='user-settings__label'>
                                <h4 className='form-group form-label'>Dark Mode</h4>
                                <span className='settings__meta form-group form-label'>Toggle the Dark Theme.</span>
                            </div>
                            <Switch
                                checked={theme === 'dark'}
                                onChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                            />
                        </Flex>
                        <Flex align='center' justify='space-between' className='user-settings__row'>
                            <div className='user-settings__label'>
                                <h4 className='form-group form-label'>Primary Color</h4>
                                <span
                                    className='settings__meta form-group form-label'>Change your system primary color.</span>
                            </div>
                            <Space>

                                <ColorPicker
                                    changeHandler={changePrimaryColor}
                                    currentColor={primary}
                                />

                                {resetP && (
                                    <Button
                                        type='default'
                                        size='middle'
                                        icon={<ArrowCounterClockwiseIcon size={24}/>}
                                        onClick={resetPrimary}
                                    />
                                )}
                            </Space>
                        </Flex>
                        {isAdminUser && (
                            <Flex align='center' justify='space-between' className='user-settings__row'>
                                <div className='user-settings__label'>
                                    <h4 className='form-group form-label'>Simple Tree Look</h4>
                                    <span className='settings__meta form-group form-label'>
                                        Use basic trunk and crown geometry for trees.
                                    </span>
                                </div>
                                <Switch
                                    checked={isSimpleTreeLook}
                                    onChange={changeSimpleTreeLook}
                                />
                            </Flex>
                        )}

                        <Flex align='start' justify='space-between' className='user-settings__row user-settings__row--units'>
                            <div className='user-settings__label user-settings__label--units'>
                                <h4 className='form-group form-label'>Unit of Measurement</h4>
                            </div>
                            <div className='user-settings__unit-grid'>
                                <div className='user-settings__unit-item'>
                                    <span className='settings__meta form-group form-label'>Roof</span>
                                    <Select
                                        placeholder='Select Unit'
                                        options={options}
                                        defaultValue={roofUnit}
                                        onChange={roofUnitChangeHandler}
                                        size='large'
                                        suffixIcon={<CaretDownIcon size={18}/>}
                                        style={{width: '100%'}}
                                    />
                                </div>
                                <div className='user-settings__unit-item'>
                                    <span className='settings__meta form-group form-label'>Tree</span>
                                    <Select
                                        placeholder='Select Unit'
                                        options={options}
                                        defaultValue={treeUnit}
                                        onChange={treeUnitChangeHandler}
                                        size='large'
                                        suffixIcon={<CaretDownIcon size={18}/>}
                                        style={{width: '100%'}}
                                    />
                                </div>
                                <div className='user-settings__unit-item'>
                                    <span className='settings__meta form-group form-label'>Other</span>
                                    <Select
                                        placeholder='Select Unit'
                                        options={options}
                                        defaultValue={unit}
                                        onChange={unitChangeHandler}
                                        size='large'
                                        suffixIcon={<CaretDownIcon size={18}/>}
                                        style={{width: '100%'}}
                                    />
                                </div>
                            </div>
                        </Flex>
                    </Space>
                </Col>
            </Row>
        </DashboardModal>
    )
}

export default UserSettings;
